import { useState } from 'react'

import { useApiClient } from '@/api/ApiClientProvider'
import {
  TRACKING_STATUS_LABELS,
  TRACKING_STATUSES,
  type CampaignChannel,
  type CampaignDetail,
  type CampaignTracking,
  type TrackingGenerateInput,
  type TrackingPatch,
} from '@/api/campaigns'
import { Card, EmptyState } from '@/design'

import { CopyField, downloadSvg, KindBadge, QrImage } from '../components'
import { Field } from '../fields'
import { useCampaignMutation, useQrSvg } from '../hooks'
import { useRunner } from '../runner'
import { landingPathError, slugify } from '../tracking'

function QrBlock({ campaign, channel, row }: { campaign: CampaignDetail['campaign']; channel: CampaignChannel; row: CampaignTracking }) {
  const qr = useQrSvg(campaign.id, row.id, Boolean(row.qrUrl))
  if (!row.qrUrl) return null
  if (qr.isPending) return <p className="camp-muted">Loading QR code…</p>
  if (qr.isError || !qr.data) return <p className="camp-error">The QR code could not be loaded.</p>
  const svg = qr.data
  return (
    <figure className="camp-tracking__qr">
      <QrImage svg={svg} label={`${channel.name} QR code`} />
      <figcaption>Opens {row.qrUrl}</figcaption>
      <button
        type="button"
        className="ci-button"
        onClick={() => downloadSvg(`${campaign.slug}-${slugify(channel.name, 'channel')}-qr.svg`, svg)}
      >
        Download {channel.name} QR code
      </button>
    </figure>
  )
}

function TrackingPanel({
  detail,
  channel,
  row,
  canManage,
}: {
  detail: CampaignDetail
  channel: CampaignChannel
  row: CampaignTracking | undefined
  canManage: boolean
}) {
  const client = useApiClient()
  const { campaign } = detail
  const [landingPath, setLandingPath] = useState(row?.landingPath ?? '')
  const { run, message } = useRunner()
  const generate = useCampaignMutation((input: TrackingGenerateInput) => client.generateCampaignTracking(campaign.id, input))
  const update = useCampaignMutation((id: string, patch: TrackingPatch) => client.updateCampaignTracking(campaign.id, id, patch))
  const busy = generate.isPending || update.isPending
  const archived = campaign.status === 'archived'
  const offline = channel.kind === 'offline'

  const mint = (input: Partial<TrackingGenerateInput>) =>
    run(
      () =>
        generate.mutateAsync([
          {
            channelId: channel.id,
            shortLink: Boolean(row?.shortCode) || (input.shortLink ?? false),
            qrCode: Boolean(row?.qrCode) || (input.qrCode ?? false),
            generatePromoCode: Boolean(row?.promoCode) || (input.generatePromoCode ?? false),
          },
        ]),
      'Tracking could not be generated.',
    )

  const pathError = landingPathError(landingPath)

  return (
    <section className="camp-panel" aria-label={`${channel.name} tracking`}>
      <div className="camp-panel__header">
        <span className="camp-inline">
          <h3 className="camp-panel__title">{channel.name}</h3>
          <KindBadge kind={channel.kind} />
        </span>
        {row && canManage ? (
          <label className="camp-inline-select">
            <span>Tracking</span>
            <select
              aria-label={`${channel.name} tracking status`}
              value={row.status}
              disabled={busy}
              onChange={(event) =>
                void run(() => update.mutateAsync([row.id, { status: event.target.value as CampaignTracking['status'] }]), 'The status could not be changed.')
              }
            >
              {TRACKING_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {TRACKING_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {!row ? (
        <div className="camp-inline">
          <span className="camp-muted">No tracking yet.</span>
          {canManage && !archived ? (
            <button type="button" className="ci-button" disabled={busy} onClick={() => void mint({ shortLink: true, qrCode: offline, generatePromoCode: offline })}>
              Generate {channel.name} tracking
            </button>
          ) : null}
        </div>
      ) : (
        <div className="camp-tracking">
          <div className="camp-panels">
            <CopyField label={`${channel.name} tracking URL`} value={row.trackingUrl} />
            {row.shortUrl ? (
              <CopyField label={`${channel.name} short link`} value={row.shortUrl} />
            ) : row.shortCode ? (
              <p className="camp-muted">Short code {row.shortCode} is reserved; its link is minted when tracking is generated on your own Insights domain.</p>
            ) : null}
            <dl className="camp-summary">
              <dt>Promo code</dt>
              <dd className="camp-code">{row.promoCode ?? 'None'}</dd>
              <dt>UTM</dt>
              <dd className="camp-code">
                {row.utmSource} / {row.utmMedium} / {row.utmCampaign}
                {row.utmContent ? ` / ${row.utmContent}` : ''}
              </dd>
            </dl>
            {canManage && !archived ? (
              <div className="camp-inline">
                {!row.shortCode ? (
                  <button type="button" className="ci-button" disabled={busy} onClick={() => void mint({ shortLink: true })}>
                    Add short link
                  </button>
                ) : null}
                {!row.qrCode ? (
                  <button type="button" className="ci-button" disabled={busy} onClick={() => void mint({ qrCode: true })}>
                    Add QR code
                  </button>
                ) : null}
                {!row.promoCode ? (
                  <button type="button" className="ci-button" disabled={busy} onClick={() => void mint({ generatePromoCode: true })}>
                    Add promo code
                  </button>
                ) : null}
              </div>
            ) : null}
            {canManage ? (
              <form
                className="camp-form"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (pathError) return
                  void run(() => update.mutateAsync([row.id, { landingPath: landingPath || null }]), 'The landing page could not be changed.')
                }}
              >
                <Field label={`${channel.name} landing path`} error={pathError} hint="Changing it rebuilds the tracking URL; short links follow.">
                  {(props) => <input {...props} value={landingPath} placeholder="/" onChange={(event) => setLandingPath(event.target.value)} />}
                </Field>
                <div className="camp-form__actions">
                  <button type="submit" className="ci-button" disabled={busy || Boolean(pathError) || landingPath === (row.landingPath ?? '')}>
                    Save landing path
                  </button>
                </div>
              </form>
            ) : null}
          </div>
          {row.qrUrl ? (
            <QrBlock campaign={campaign} channel={channel} row={row} />
          ) : row.qrCode ? (
            <p className="camp-muted camp-tracking__qr">The QR code is reserved; it can be drawn once its link is minted on your own Insights domain.</p>
          ) : null}
        </div>
      )}
      {message}
    </section>
  )
}

/** Each channel's tracking: the URL, short link, QR code and promo code, ready to hand to whoever runs the channel. */
export function TrackingCard({ detail, canManage }: { detail: CampaignDetail; canManage: boolean }) {
  return (
    <Card title="Tracking" subtitle="Per channel: a UTM-tagged link, a short link, a QR code and a promo code, so offline channels attribute like digital ones.">
      {detail.channels.length === 0 ? <EmptyState variant="no-data" compact title="No channels" description="This campaign has no channels to track." /> : null}
      <div className="camp-panels">
        {detail.channels.map((channel) => (
          <TrackingPanel
            key={channel.id}
            detail={detail}
            channel={channel}
            row={detail.tracking.find((candidate) => candidate.channelId === channel.id && candidate.creativeId === null)}
            canManage={canManage}
          />
        ))}
      </div>
    </Card>
  )
}
